<template>
  <div class="good-practices-page">
    <WeCircularNavbar />

    <section class="page-section">
      <div class="page-container">
        <h1 class="page-title">Good practices</h1>

        <p class="page-intro">
          Get inspired by successful initiatives and projects driving innovation and sustainable impact.
        </p>

        <div class="filters">
          <div class="search-box">
            <span class="search-box__icon">⌕</span>
            <input v-model.trim="searchTerm" type="text" class="search-box__input" placeholder="Search" />
          </div>

          <select v-model="selectedCountry" class="filter-select">
            <option value="">Select Country</option>
            <option v-for="c in countries" :key="c" :value="c">{{ c }}</option>
          </select>

          <select v-model="selectedTopic" class="filter-select">
            <option value="">Select topic</option>
            <option v-for="t in topics" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>

        <div class="cards-grid">
          <article v-for="(item, index) in paginatedItems" :key="'gp-' + index" class="card">
            <div class="card__image-wrapper">
              <img :src="item.image || defaultCardImage" :alt="item.name" class="card__image" />
            </div>

            <div class="card__body">
              <h3 class="card__title">
                {{ item.name }}
              </h3>

              <p class="card__meta"><strong>Duration:</strong> {{ item.duration || '-' }}</p>

              <p class="card__meta"><strong>Location:</strong> {{ item.countryRegion || '-' }}</p>
            </div>

            <router-link :to="`/we-circular/good-practices/${slugify(item.name)}`" class="card__footer-link">
              Learn More
              <span class="card__footer-arrow">→</span>
            </router-link>
          </article>
        </div>

        <div v-if="totalPages > 1" class="pagination-dots">
          <button
            v-for="page in totalPages"
            :key="page"
            type="button"
            class="pagination-dot"
            :class="{ active: currentPage === page }"
            @click="currentPage = page"
          ></button>
        </div>
      </div>
    </section>

    <WeCircularFooter />
  </div>
</template>

<script>
import WeCircularNavbar from './WeCircularNavbar.vue'
import WeCircularFooter from './WeCircularFooter.vue'
import { goodPracticesData } from './goodPracticesData.js'
import defaultCardImage from '../../assets/img/we-circular/good-practices/1-1@4x.png'

export default {
  name: 'GoodPracticesPage',
  components: {
    WeCircularNavbar,
    WeCircularFooter
  },
  data() {
    return {
      searchTerm: '',
      selectedCountry: '',
      selectedTopic: '',
      currentPage: 1,
      itemsPerPage: 12,
      defaultCardImage
    }
  },
  computed: {
    allItems() {
      if (!goodPracticesData || typeof goodPracticesData !== 'object') {
        return []
      }

      return Object.values(goodPracticesData).flat()
    },

    countries() {
      return [...new Set(this.allItems.map(item => item.countryRegion).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      )
    },

    topics() {
      return [...new Set(this.allItems.map(item => item.topic).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    },

    filteredItems() {
      const term = String(this.searchTerm || '').toLowerCase()

      return this.allItems.filter(item => {
        const matchesSearch =
          !term ||
          [item.name, item.description, item.countryRegion, item.topic, item.duration].some(value =>
            String(value || '')
              .toLowerCase()
              .includes(term)
          )

        const matchesCountry = !this.selectedCountry || item.countryRegion === this.selectedCountry
        const matchesTopic = !this.selectedTopic || item.topic === this.selectedTopic

        return matchesSearch && matchesCountry && matchesTopic
      })
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.itemsPerPage))
    },

    paginatedItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      return this.filteredItems.slice(start, start + this.itemsPerPage)
    }
  },
  methods: {
    slugify(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    }
  },
  watch: {
    searchTerm() {
      this.currentPage = 1
    },
    selectedCountry() {
      this.currentPage = 1
    },
    selectedTopic() {
      this.currentPage = 1
    }
  }
}
</script>

<style scoped>
.good-practices-page {
  min-height: 100vh;
  background: #f3f3f3;
}

.page-section {
  width: 100%;
}

.page-container {
  width: min(1320px, calc(100% - 48px));
  margin: 0 auto;
  padding: 36px 0 60px;
  box-sizing: border-box;
}

.page-title {
  margin: 0 0 14px;
  color: #003399;
  font-size: 48px;
  line-height: 1.06;
  font-weight: 600;
  letter-spacing: -0.03em;
  text-align: left;
}

.page-intro {
  max-width: 420px;
  margin: 0 0 28px;
  color: #0e6eb6;
  font-size: 16px;
  line-height: 1.55;
  text-align: left;
}

/* FILTERS */

.filters {
  display: grid;
  grid-template-columns: minmax(220px, 1.2fr) minmax(180px, 1fr) minmax(180px, 1fr);
  gap: 12px;
  margin-bottom: 26px;
}

.search-box,
.filter-select {
  height: 40px;
  border: 1px solid #8fd0cb;
  border-radius: 999px;
  background: #eef8f7;
  color: #2d5c67;
  box-sizing: border-box;
}

.search-box {
  display: flex;
  align-items: center;
  padding: 0 12px;
}

.search-box__icon {
  margin-right: 10px;
  color: #6a8a89;
  font-size: 14px;
  line-height: 1;
}

.search-box__input {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #2d5c67;
  font-size: 14px;
}

.search-box__input::placeholder {
  color: #7da5a5;
}

.filter-select {
  padding: 0 12px;
  outline: none;
  font-size: 14px;
}

/* CARDS */

.cards-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.card {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  border: 2px solid #2b7475;
  border-radius: 28px;
  overflow: hidden;
  background: #f8f8f5;
}

.card__image-wrapper {
  padding: 22px 22px 0;
}

.card__image {
  display: block;
  width: 100%;
  height: 195px;
  object-fit: cover;
  border-radius: 22px;
  background: #d7dfdf;
}

.card__body {
  flex: 1;
  padding: 18px 22px 20px;
}

.card__title {
  margin: 0 0 22px;
  color: #003399;
  font-size: 24px;
  line-height: 1.12;
  font-weight: 500;
  letter-spacing: -0.02em;
  text-align: left;
}

.card__meta {
  margin: 0 0 10px;
  color: #003399;
  font-size: 15px;
  line-height: 1.5;
  text-align: left;
}

.card__meta strong {
  font-weight: 700;
}

.card__footer-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  min-height: 74px;
  padding: 0 22px;
  background: #0e5a58;
  background-image: url('../../assets/img/we-circular/9-16@4x.png');
  background-size: cover;
  background-position: center;
  color: #ffffff;
  text-decoration: none;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.card__footer-link:hover {
  opacity: 0.96;
}

.card__footer-arrow {
  font-size: 24px;
  line-height: 1;
}

/* PAGINATION */

.pagination-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 26px;
}

.pagination-dot {
  width: 10px;
  height: 10px;
  border: none;
  border-radius: 50%;
  background: #c6ced3;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.pagination-dot.active {
  background: #18baa8;
  transform: scale(1.08);
}

/* RESPONSIVE */

@media (max-width: 1280px) {
  .cards-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .filters {
    grid-template-columns: 1fr;
  }

  .cards-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .page-title {
    font-size: 38px;
  }

  .card__title {
    font-size: 20px;
  }
}

@media (max-width: 640px) {
  .page-container {
    width: min(100% - 24px, 1320px);
    padding: 28px 0 44px;
  }

  .page-title {
    font-size: 30px;
  }

  .page-intro {
    max-width: 100%;
    font-size: 14px;
  }

  .cards-grid {
    grid-template-columns: 1fr;
  }

  .card__image-wrapper {
    padding: 18px 18px 0;
  }

  .card__image {
    height: 190px;
    border-radius: 18px;
  }

  .card__body {
    padding: 16px 18px 18px;
  }

  .card__title {
    font-size: 18px;
    margin-bottom: 18px;
  }

  .card__meta {
    font-size: 14px;
  }

  .card__footer-link {
    min-height: 62px;
    padding: 0 18px;
    font-size: 16px;
  }
}
</style>
